import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';

import { DisplayContactPerson, Employer } from '@pflegia/data-access';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import useGetCemContactDetails from '@pflegia/ui/pflegia/organisation/hooks/use-get-cem-contact-details';
import { CardStatus, CardType } from '@pflegia/ui/pflegia/types/card';
import AnonymousWrapper from '@pflegia/ui/shared/wrapper/AnonymousWrapper';
import { formatNameWithTitles } from '@pflegia/util/functions/format-name';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import ImageWithShimmer from '@components/image/ImageWithShimmer';
import SkeletonDivDefault from '@components/skeleton/SkeletonDivDefault';

import NoContactPerson from '@public/assets/no-contact-person.png';

import { FUNNEL } from '@shared/application/constants';
import OffersDialogueButton from '@shared/components/dialogue';

import CemPlusContactInfo from './CemPlusContactInfo';

export interface ContactPersonCardProps {
  anonymous?: boolean;
  contactPerson?: Pick<DisplayContactPerson, 'jobTitle' | 'name' | 'pictureKey' | 'quote' | 'surname'>;
  jobPositionId?: string;
  jobPositionTitle: string;
  jobPositionSlug: string;
  status: CardStatus;
  type: CardType;
  employer: Pick<Employer, 'formalLanguage' | 'organisation' | 'organisationId' | 'publicContactActivated' | 'name'>;
  openDialog: (connectionInfo: ConnectionInfo) => void;
}

const ContactPersonCard = ({
  anonymous = false,
  contactPerson,
  jobPositionId,
  jobPositionTitle,
  jobPositionSlug,
  status,
  type,
  employer,
  openDialog,
}: ContactPersonCardProps): React.JSX.Element => {
  const { t } = useTranslation('jobDetails', { keyPrefix: 'contactPerson' });
  const publicContactDetails = useGetCemContactDetails(employer, anonymous);

  const label = t(type === 'default' ? 'apply' : `${type}.${status}`);
  const { publicContactActivated } = employer ?? {};
  const { loading } = publicContactDetails;
  const { publicEmail, publicPhoneNumber } = publicContactDetails.cemPlusContactInfo ?? {};
  const cemPlusContactInfoVisible = publicContactActivated && (publicEmail || publicPhoneNumber);

  return (
    <div className='flex h-full flex-col justify-between rounded-2xl bg-white px-6 py-4'>
      <div className='mb-6'>
        <h2 className='font-handwriting text-main-blue mb-4 ml-17.25 text-3xl font-normal'>{t('title')}</h2>
        <AnonymousWrapper anonymous={anonymous} href={FUNNEL}>
          <div className='mb-6 flex min-h-12.5 flex-row items-center space-x-6'>
            <div className='relative aspect-square h-27 w-27'>
              {contactPerson?.pictureKey ? (
                <ImageWithShimmer
                  alt={t('contactPerson.title')}
                  className='rounded-2xl object-cover'
                  src={getPublicImageUrl(contactPerson.pictureKey) as string}
                  fill
                  sizes='108px'
                />
              ) : (
                <Image alt={t('contactPerson.title')} className='rounded-2xl object-cover' src={NoContactPerson} fill sizes='108px' />
              )}
            </div>
            <div className='flex flex-col'>
              <span className='text-main-blue text-lg font-semibold'>
                {formatNameWithTitles(contactPerson, `${t('contactPersonFirstName')} ${t('contactPersonSurName')}`)}
              </span>
              <span className='text-default text-sm'>{contactPerson?.jobTitle}</span>
            </div>
          </div>
        </AnonymousWrapper>
        {contactPerson?.quote && <span className='non-italic text-default text-sm leading-5'>{`„${contactPerson?.quote}“`}</span>}
      </div>
      <div className='flex flex-col space-y-6 xl:w-full xl:flex-row xl:items-center xl:justify-between xl:space-y-0'>
        {loading ? (
          <SkeletonDivDefault className='h-20' />
        ) : (
          <>
            {cemPlusContactInfoVisible ? (
              <CemPlusContactInfo
                anonymous={anonymous}
                loading={loading}
                publicPhoneNumber={publicPhoneNumber}
                publicEmail={publicEmail}
                formalLanguage={employer.formalLanguage}
              />
            ) : (
              <>
                {type === 'default' && (
                  <span className='text-default hidden flex-1 pr-4 text-xs leading-5 xl:block'>{t('applySubtitle')}</span>
                )}
                <OffersDialogueButton
                  jobPositionId={jobPositionId || ''}
                  label={label}
                  status={status}
                  jobTitle={jobPositionTitle}
                  type={type}
                  employerName={employer.name}
                  className={clsx(type !== 'default' && 'w-full')}
                  openDialog={openDialog}
                  slug={jobPositionSlug}
                />
                {type === 'default' && (
                  <span className='text-default w-full flex-1 pr-4 text-center text-xs leading-5 xl:hidden'>{t('applySubtitle')}</span>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContactPersonCard;
